import cookieManager from "./cookie.js"
import { isAndroidApp, isIOSApp } from "./app-type-checker.js"
import {isDebug} from "./debug.js"
import { getMembershipFromQuery, getMembershipFromUrl } from "../purchase/purchase.js"
import { setSessionId } from "./auth-storage.js"

type keyofFeatureActivations = string

export interface AppInterface {
    version(): number
    buildDate(): number
    featureActivations: Record<keyofFeatureActivations, {
        android: number | null,
        iOS: number | null,
        buildDate: number | null,
    }>
    isAllowedTo(title: keyofFeatureActivations): boolean
    [x: string]: any
}

const app: AppInterface = {
    // TODO: Wrap this
    ALWAYS: 0,
    NEVER: null,
    CHECKBUILDDATE: -1,

    // NOTE: For android app, Do NOT use android version due to android app lacking fixed global version number between variations.
    //       Instead, USE ONLY buildDate.
    // USAGE: This is to check app info to find out if the contents is allowed to show.
    // APP.featureActivations.feature_name = { "android": [ANDROID_APP_VERSION], "iOS": [IOS_APP_VERSION], "buildDate": [APP_BUILD_DATE(yyMMdd)] } }
    // [ANDROID/IOS_APP_VERSION, APP_BUILD_DATE(yyMMdd)] : the minimum number to allow
    //          APP.NEVER           : never allowed
    //          APP.ALWAYS          : allowed always
    //          APP.CHECKBUILDDATE  : allowed only when the app has built since buildDate(yyMMdd)
    // e.g) APP.featureActivations = { ...APP.featureActivations
    //            ,"androidOnly" : { "android": APP.ALWAYS,  "iOS": APP.NEVER, "buildDate": APP.NEVER }
    //            ,"showSth" : { "android": APP.NEVER, "iOS": 920,  "buildDate": APP.NEVER }
    //            ,"UseBuildDateOnly": { "android": APP.CHECKBUILDDATE, "iOS": APP.CHECKBUILDDATE, "buildDate": 200317 } }
    //            ,"DebugWebBrowserOnly": { "android": APP.NEVER, "iOS": APP.NEVER, "buildDate": 200620 } and, set appBuildDate in cookie larger than 'buildDate' (e.g. 200621)
    featureActivations: {},

    isAllowedTo: (title:string):boolean => {
        const minNum = app.featureActivations[title]
        if (minNum == undefined) return false
        const specMinVer:number|null = isAndroidApp() ? minNum.android : minNum.iOS
        if(isDebug.web() || specMinVer == app.CHECKBUILDDATE) return app.shouldAllow(app.buildDate(), minNum.buildDate)
        return app.shouldAllow(app.version(), specMinVer)
    },
    shouldAllow: (my:number, spec:number|null):boolean => {
        return (spec != null && my >= spec) || spec == app.ALWAYS
    },
    version: ():number => Number(cookieManager.get("appVersion")),
    buildDate: ():number => Number(cookieManager.get("appBuildDate")),
    saveAppInfoInCookie: ():void => {
        cookieManager.set('appVersion', app.getParameterFromUrl('appVersion'))
        cookieManager.set('appBuildDate', app.getParameterFromUrl('appBuildDate'))
        setSessionId(app.getParameterFromUrl('sessionId'))
    },
    getParameterFromUrl: (name:string):string|null => {
        return app.getParameterFromQuery(name, window.location.search)
    },
    getParameterFromQuery : (name: string, queryParameters: string = ''): string | null => {
        const params = new URLSearchParams(queryParameters);
        const value = params.get(name);
        if (value === null) return null
        if (value === '') return ''
        return decodeURIComponent(value.replace(/\+/g, " "))
    }
}

Object.assign(app.featureActivations, {
    "containMySheetMusics": { "android": app.ALWAYS, "iOS": 1003, "buildDate": app.ALWAYS },
    "goTuner": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 220627 },
    "deletableHistoryAndMostPlayedEntries": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 240501 },
    "timerSyncWithHostApp": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 240720 }, // older implementation of timer uses cookies and is buggy for android
    "updateDailyPracticeData": { "android": app.CHECKBUILDDATE, "iOS": app.NEVER, "buildDate": 251120 }
});

export interface AppExtension {
    isRecording: () => boolean;
    isPlayingMetronome: () => boolean;
    hasStore: () => boolean;
    getMembershipInfoFromUrl: () => void;
    getContainMySheetMusicsInfoFromUrl: () => void;
    getMembershipInfoFromQuery: (param: string) => void;
    isAllowedToMoveBounds: () => boolean;
    setMembershipAndStore(membership: string, hasStore: string): void;
    isAllowedToContainMySheetMusics(): boolean;
    containMySheetMusic: boolean
}

const appExtension: AppExtension = {
    isRecording: (): boolean => {
        try {
            //  TODO: leave cookieManager.get("isRecording") == "T" only.
            return ((isDebugMode || isIOSApp()) && cookieManager.get("isRecording") == "T" ) || (isAndroidApp() && HostApp!.isRecording())
        }
        catch (error) {
            return false
        }
    },
    isPlayingMetronome: (): boolean => {
        try {
            //  TODO: leave cookieManager.get("isPlayingMetronome") == "T" only.
            return (isAndroidApp() && HostApp!.isPlayingMetronome()) || (isIOSApp() && cookieManager.get("isPlayingMetronome") == "T")
        }
        catch (error) {
            return false
        }
    },
    hasStore: (): boolean => {
        return isDebugMode || localStorage.getItem("hasStore") == "yes"
    },
    setMembershipAndStore(membership: string, hasStore: string): void {
        localStorage.setItem("membership", membership);
        localStorage.setItem("hasStore", hasStore);
        // NOTE: if app doesn't pass 'hasStore=yes', then to move bounds is not allowed. 
        localStorage.setItem("allowedToMoveBounds", isDebugMode || hasStore == "yes" ? "T" : "F")
    },
    getMembershipInfoFromUrl: (): void => {
        const membership: string = getMembershipFromUrl() ?? ''
        const hasStore: string = app.getParameterFromUrl('hasStore') ?? ''
        appExtension.setMembershipAndStore(membership, hasStore)
    },
    getContainMySheetMusicsInfoFromUrl: (): void => {
        const containMySheetMusics: string = app.getParameterFromUrl('containMySheetMusics') ?? ''
        localStorage.setItem("allowedToContainMySheetMusics", containMySheetMusics == "yes" ? "T" : "F")
    },
    isAllowedToContainMySheetMusics(): boolean {
        return !isIOSApp() || (app.isAllowedTo("containMySheetMusics") && localStorage.getItem("allowedToContainMySheetMusics") === "T");
    },
    get containMySheetMusic(): boolean {
        return appExtension.isAllowedToContainMySheetMusics()
    },
    getMembershipInfoFromQuery: (param: string): void => {
        const membership: string = getMembershipFromQuery(param) ?? ''
        const hasStore: string = app.getParameterFromQuery('hasStore', param) ?? ''
        appExtension.setMembershipAndStore(membership, hasStore)
    },
    isAllowedToMoveBounds: (): boolean =>  isDebugMode || localStorage.getItem("allowedToMoveBounds") == "T"
};
const isDebugMode = import.meta.env.VITE_DEBUG_MODE === "true";

Object.assign(app, appExtension);
export default app as (AppInterface & AppExtension);